import BtEditableBox from "@common-components/tasks/bt-editable-box/bt-editable-box";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { resetFlowState, setCurrentBot, setIsDraft, setIsInitialLoad } from "@common/reducers/ChatbotReducer";
import { getCurrentBotSelector, getIsDraftOlderThanDeploymentSelector } from "@common/reducers/ChatbotSelectors";
import { Check, Edit as EditIcon, Settings as SettingsIcon, FileUploadOutlined as UploadIcon, Info as InfoIcon } from '@mui/icons-material';
import { IconButton, Modal, Stack, ThemeProvider, Tooltip, createTheme } from "@mui/material";
import { saveAs } from 'file-saver';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BTbotbutton from "./bot-components/bt-bot-button";
import BTChatbotModal from "./bot-components/bt-chatbot-modal";
import { DeployButtonWrapper } from './bot-components/deploy-button-wrapper';
import { SaveButton } from './bot-components/save-button';
import { BotStatusForEditor } from "./bot-status-for-editor";
import ConnectedEngines from "../connected-engines/connected-engines";
import { namespace } from "@common/config/constants";

const inputTheme = {
    padding: "0 10px",
    maxWidth: "280px",
    fontSize: "22px",
    fontWeight: "600",
    height: '33px',
    borderRadius: "8px",
    border: "1px solid var(--bot-build-title-input-border)",
    backgroundColor: "var(--bot-build-title-input-bg)",
    color: "var(--bot-build-title-text)",
    "& .MuiInputBase-input": {
        padding: "0",
        color: "var(--bot-build-title-text)",
    },
};

const textTheme = {
    fontSize: "22px",
    padding: "0 10px",
    borderRadius: "8px",
    border: "1px dashed transparent",
    fontWeight: "600",
    color: "var(--bot-build-title-text)",
    "&:hover": {
        border: "1px dashed var(--bot-build-title-hover-border)",
    },
    height: '33px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '280px',
    boxSizing: 'border-box',
    lineHeight: '34px',
};

const BotHeader = React.memo(() => {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch();
    const currentBot = useAppSelector(state => getCurrentBotSelector(state));
    const nodes = useAppSelector(state => state.ChatbotReducer.nodes);
    const edges = useAppSelector(state => state.ChatbotReducer.edges);
    const isDraft = useAppSelector(state => state.ChatbotReducer.isDraft);
    const isDraftOlderThanDeployment = useAppSelector(state => getIsDraftOlderThanDeploymentSelector(state));

    const [modalOpen, setModalOpen] = useState(false);
    const [editBotName, setEditBotName] = useState(false);


    const exportBotAsJSON = useCallback(() => {
        const jsonString = JSON.stringify({ nodes, edges }, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const fileName = `${currentBot?.name || t('chatbot.defaultFlowFileName')}.json`;
        saveAs(blob, fileName);
    }, [nodes, edges, currentBot?.name, t]);


    const theme = useMemo(() => createTheme({
        components: {
            MuiTooltip: {
                defaultProps: {
                    PopperProps: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, -10],
                                },
                            },
                        ],
                    },
                },
            },
        },
    }), []); // Theme doesn't depend on any props, so memoize it

    const handleSettingsClick = useCallback(async () => {
        setModalOpen(true);
        await dispatch(setCurrentBot(currentBot));
    }, [dispatch, currentBot]);

    const handleBotNameUpdate = useCallback(async (value: string) => {
        if (currentBot) {
            dispatch(setCurrentBot({
                ...currentBot,
                name: value === '' ? currentBot?.name : value
            }));
        }
    }, [currentBot, dispatch, isDraft]);

    const handleEditModeToggle = useCallback(() => {
        setEditBotName(prev => !prev);
    }, []);

    const botStatus = useCallback(() => {
        if (isDraft && currentBot?.activeDeploymentId) {
            return 'active with draft changes'
        }
        if (isDraft && !currentBot?.activeDeploymentId) {
            return 'not active with draft changes'
        }
        if (!isDraft && currentBot?.activeDeploymentId) {
            return 'active'
        }
        if (!isDraft && !currentBot?.activeDeploymentId) {
            return 'not active'
        }
    }, [isDraft, currentBot?.activeDeploymentId])


    const shouldDisplaySaveDraftButton = useMemo(() => {
        return ['active with draft changes', 'not active with draft changes'].includes(botStatus() ?? '')
    }, [botStatus]);

    const shouldDisplayDeployButton = useMemo(() => {
        return !['active'].includes(botStatus() ?? '') || isDraftOlderThanDeployment
    }, [botStatus, isDraftOlderThanDeployment]);


    // Handle initial load state and cleanup
    useEffect(() => {
        // Reset flow state on mount
        dispatch(resetFlowState());

        // Set initial load to false after a small delay to allow initial node positioning
        const timer = setTimeout(() => {
            dispatch(setIsInitialLoad(false));
        }, 500);

        // Cleanup function that runs on unmount
        return () => {
            clearTimeout(timer);
            dispatch(resetFlowState());
        };
    }, [dispatch]); // Add dispatch to dependencies

    return (
        <ThemeProvider theme={theme}>
            <Stack direction="row" sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px',
                backgroundColor: 'var(--color-surface, #ffffff)',
                marginBottom: '10px',
            }}>
                <Stack direction="row" spacing={2} style={{
                    fontSize: '22px',
                    fontWeight: '600',
                    color: 'var(--color-text, #111827)',
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <BtEditableBox
                        inputSx={inputTheme}
                        boxSx={textTheme}
                        text={currentBot?.name}
                        forceEditMode={editBotName}
                        onBlur={() => setEditBotName(false)}
                        onValueUpdate={handleBotNameUpdate}
                    />
                    {!editBotName && (
                        <IconButton onClick={handleEditModeToggle} disableRipple>
                            <EditIcon sx={{ color: 'var(--color-text, #111827)', fontSize: '21px', padding: '2px' }} />
                        </IconButton>
                    )}
                    {editBotName && (
                        <IconButton onClick={handleEditModeToggle} disableRipple>
                            <Check sx={{ color: 'var(--color-text, #111827)', fontSize: '21px', padding: '2px' }} />
                        </IconButton>
                    )}
                    {currentBot && (
                        <>
                            <BotStatusForEditor />
                        </>
                    )}
                </Stack>

                <Stack direction="row" spacing={1.25} alignItems="center">
                    {shouldDisplaySaveDraftButton &&
                        <SaveButton disabled={!isDraft} />
                    }

                    {shouldDisplayDeployButton &&
                        <DeployButtonWrapper
                            onAfterDeploy={() => dispatch(setIsDraft(false))}
                        />
                    }

                    <Tooltip placement="top" title={t('chatbot.settingsTooltip')}>
                        <BTbotbutton
                            styleType='empty'
                            onClick={handleSettingsClick}
                            sx={{
                                minWidth: 38,
                                width: 38,
                                padding: 0,
                            }}
                        >
                            <SettingsIcon sx={{ fontSize: 18 }} />
                        </BTbotbutton>
                    </Tooltip>

                    <Tooltip placement="top" title={t('chatbot.exportFlowTooltip')}>
                        <BTbotbutton
                            styleType='empty'
                            onClick={exportBotAsJSON}
                            sx={{
                                minWidth: 38,
                                width: 38,
                                padding: 0,
                            }}
                        >
                            <UploadIcon sx={{ fontSize: 18 }} />
                        </BTbotbutton>
                    </Tooltip>

                    <Tooltip placement="top" title={t('chatbot.systemGuideTooltip')}>
                        <BTbotbutton
                            styleType='empty'
                            startIcon={<InfoIcon />}
                            onClick={() => window.open('https://blueticks.co/guides/bot-system', '_blank', 'noopener,noreferrer')}
                        >
                            {t('chatbot.systemGuide')}
                        </BTbotbutton>
                    </Tooltip>

                    <ConnectedEngines />

                </Stack>

                <Modal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                >
                    <div>
                        <BTChatbotModal type="setSettings" onClose={() => setModalOpen(false)} />
                    </div>
                </Modal>
            </Stack>
        </ThemeProvider>
    );
});

BotHeader.displayName = 'BotHeader';

export default BotHeader;
